import type { Quote } from "@/types/quote";
import { Separator } from "@/components/ui/separator";

interface QuotePreviewProps {
	quote: Quote;
	totals: { subtotal: number; totalDiscount: number; totalTax: number; total: number };
}

function formatCurrency(amount: number, currency: string) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

function formatDate(dateStr: string) {
	if (!dateStr) return "";
	return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export function QuotePreview({ quote, totals }: QuotePreviewProps) {
	return (
		<div className="max-w-3xl mx-auto">
			<div
				id="quote-preview"
				className="bg-zinc-800/70 border border-zinc-700/50 rounded-xl shadow-lg shadow-black/20 overflow-hidden"
			>
				{/* Header accent bar */}
				<div className="h-1.5 bg-blue-600" />

				<div className="p-12">
					{/* Title section */}
					<div className="flex items-start justify-between mb-16">
						<div>
							<h1 className="text-3xl font-light tracking-tight text-zinc-100 mb-1">
								{quote.title || "Proposal"}
							</h1>
							<p className="text-sm text-zinc-500 font-mono">{quote.quoteNumber}</p>
						</div>
						<div className="text-right">
							<div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
								Date
							</div>
							<p className="text-sm text-zinc-300">{formatDate(quote.date)}</p>
							<div className="text-xs uppercase tracking-wider text-zinc-500 mt-3 mb-1">
								Valid Until
							</div>
							<p className="text-sm text-zinc-300">{formatDate(quote.validUntil)}</p>
						</div>
					</div>

					{/* From / To */}
					<div className="grid grid-cols-2 gap-16 mb-16">
						<div>
							<div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
								From
							</div>
							<p className="text-sm font-medium text-zinc-100 mb-1">{quote.company.name}</p>
							{quote.company.email && (
								<p className="text-sm text-zinc-400">{quote.company.email}</p>
							)}
							{quote.company.phone && (
								<p className="text-sm text-zinc-400">{quote.company.phone}</p>
							)}
							{quote.company.address && (
								<p className="text-sm text-zinc-400 whitespace-pre-line mt-1">
									{quote.company.address}
								</p>
							)}
						</div>
						<div>
							<div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
								To
							</div>
							<p className="text-sm font-medium text-zinc-100 mb-1">
								{quote.client.name || "Client Name"}
							</p>
							{quote.client.company && (
								<p className="text-sm text-zinc-400">{quote.client.company}</p>
							)}
							{quote.client.email && (
								<p className="text-sm text-zinc-400">{quote.client.email}</p>
							)}
							{quote.client.phone && (
								<p className="text-sm text-zinc-400">{quote.client.phone}</p>
							)}
						</div>
					</div>

					{/* Line Items */}
					{quote.items.length > 0 && (
						<div className="mb-12">
							<table className="w-full">
								<thead>
									<tr className="border-b border-zinc-700">
										<th className="text-left text-xs uppercase tracking-wider text-zinc-500 pb-3 font-medium">
											Description
										</th>
										<th className="text-right text-xs uppercase tracking-wider text-zinc-500 pb-3 font-medium w-20">
											Qty
										</th>
										<th className="text-right text-xs uppercase tracking-wider text-zinc-500 pb-3 font-medium w-28">
											Unit Price
										</th>
										{quote.items.some((i) => i.discount > 0) && (
											<th className="text-right text-xs uppercase tracking-wider text-zinc-500 pb-3 font-medium w-20">
												Disc.
											</th>
										)}
										<th className="text-right text-xs uppercase tracking-wider text-zinc-500 pb-3 font-medium w-28">
											Amount
										</th>
									</tr>
								</thead>
								<tbody>
									{quote.items.map((item, i) => {
										const lineTotal = item.quantity * item.unitPrice;
										const discountAmt = lineTotal * (item.discount / 100);
										const afterDiscount = lineTotal - discountAmt;
										const taxAmt = afterDiscount * (item.tax / 100);
										const total = afterDiscount + taxAmt;

										return (
											<tr
												key={item.id}
												className={
													i < quote.items.length - 1 ? "border-b border-zinc-700/30" : ""
												}
											>
												<td className="py-3.5 text-sm text-zinc-300 pr-4">
													{item.description || "Untitled item"}
												</td>
												<td className="py-3.5 text-sm text-zinc-400 text-right font-mono">
													{item.quantity}
												</td>
												<td className="py-3.5 text-sm text-zinc-400 text-right font-mono">
													{formatCurrency(item.unitPrice, quote.currency)}
												</td>
												{quote.items.some((i) => i.discount > 0) && (
													<td className="py-3.5 text-sm text-zinc-400 text-right font-mono">
														{item.discount > 0 ? `${item.discount}%` : "\u2014"}
													</td>
												)}
												<td className="py-3.5 text-sm text-zinc-100 text-right font-mono font-medium">
													{formatCurrency(total, quote.currency)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>

							{/* Totals */}
							<div className="mt-6 pt-4 border-t border-zinc-700">
								<div className="flex flex-col items-end space-y-1.5">
									<div className="grid grid-cols-2 gap-8 text-sm">
										<span className="text-zinc-500 text-right">Subtotal</span>
										<span className="text-zinc-300 text-right font-mono">
											{formatCurrency(totals.subtotal, quote.currency)}
										</span>
									</div>
									{totals.totalDiscount > 0 && (
										<div className="grid grid-cols-2 gap-8 text-sm">
											<span className="text-zinc-500 text-right">Discount</span>
											<span className="text-emerald-400 text-right font-mono">
												-{formatCurrency(totals.totalDiscount, quote.currency)}
											</span>
										</div>
									)}
									{totals.totalTax > 0 && (
										<div className="grid grid-cols-2 gap-8 text-sm">
											<span className="text-zinc-500 text-right">Tax</span>
											<span className="text-zinc-300 text-right font-mono">
												{formatCurrency(totals.totalTax, quote.currency)}
											</span>
										</div>
									)}
									<Separator className="w-48 my-2" />
									<div className="grid grid-cols-2 gap-8">
										<span className="text-zinc-100 text-right font-medium text-sm">
											Total
										</span>
										<span className="text-zinc-100 text-right text-xl font-semibold font-mono">
											{formatCurrency(totals.total, quote.currency)}
										</span>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Terms */}
					{quote.terms.length > 0 && (
						<div className="mb-12">
							<h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-medium">
								Terms & Conditions
							</h2>
							<div className="space-y-2">
								{quote.terms.map((term, i) => (
									<div key={i} className="flex items-start gap-3">
										<span className="text-zinc-600 text-sm font-mono w-5 shrink-0">
											{i + 1}.
										</span>
										<p className="text-sm text-zinc-300 leading-relaxed">{term}</p>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Notes */}
					{quote.notes && (
						<div className="mb-12">
							<h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-3 font-medium">
								Notes
							</h2>
							<p className="text-sm text-zinc-300 leading-relaxed">{quote.notes}</p>
						</div>
					)}

					{/* Signature */}
					{quote.signature && (
						<div className="pt-8 border-t border-zinc-700/50">
							<div className="flex items-end justify-between">
								<div>
									<div className="border-b border-zinc-600 pb-2 mb-2 min-w-[200px]">
										{quote.signature.type === "draw" ? (
											<img
												src={quote.signature.data}
												alt="Signature"
												className="h-12 object-contain"
											/>
										) : (
											<span
												className="text-2xl text-zinc-200"
												style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
											>
												{quote.signature.data}
											</span>
										)}
									</div>
									<p className="text-sm text-zinc-300">{quote.signature.name}</p>
									<p className="text-xs text-zinc-500">{quote.signature.date}</p>
								</div>
								<div className="text-right">
									<div className="border-b border-zinc-700 pb-2 mb-2 min-w-[200px]" />
									<p className="text-xs text-zinc-500">Client Signature</p>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="px-12 py-6 bg-zinc-900/50 border-t border-zinc-700/50 text-center">
					<p className="text-xs text-zinc-500">
						Generated with QuoteCraft — Professional Proposals Made Simple
					</p>
				</div>
			</div>
		</div>
	);
}
